import { AccountHolder, Payment } from '../types/index';

export type SystemMappingAccountType = 'Current' | 'Savings';

export const CURRENT_SYSTEM_FEE_CATEGORIES = ['Monthly Account Fees', 'Cash Deposit Charges', 'Other System Charges'];
export const SAVINGS_SYSTEM_FEE_CATEGORIES = ['System Fees', 'General'];

export const SYSTEM_FEE_CATEGORIES = CURRENT_SYSTEM_FEE_CATEGORIES;
export const ALL_SYSTEM_FEE_CATEGORIES = Array.from(new Set([...CURRENT_SYSTEM_FEE_CATEGORIES, ...SAVINGS_SYSTEM_FEE_CATEGORIES]));

export const getSystemFeeCategoriesForAccountType = (accountType: SystemMappingAccountType): string[] => {
  return accountType === 'Savings' ? SAVINGS_SYSTEM_FEE_CATEGORIES : CURRENT_SYSTEM_FEE_CATEGORIES;
};

const LEGACY_SYSTEM_CATEGORY_ALIASES: Record<string, string> = {
  'Other Bank Charges': 'Other System Charges',
  'System Fee': 'System Fees',
  'General Interest': 'General',
};

const CATEGORY_NORMALIZATION_LOOKUP = new Map<string, string>([
  ...ALL_SYSTEM_FEE_CATEGORIES.map((category) => [category.trim().toLowerCase(), category] as const),
  ...Object.entries(LEGACY_SYSTEM_CATEGORY_ALIASES).map(([legacy, canonical]) => [legacy.trim().toLowerCase(), canonical] as const),
]);

export const normalizeSystemFeeCategoryName = (name?: string): string | null => {
  if (!name) return null;
  return CATEGORY_NORMALIZATION_LOOKUP.get(name.trim().toLowerCase()) || null;
};

export const DEFAULT_SYSTEM_CATEGORY_KEYWORDS: Record<string, string[]> = {
  'Monthly Account Fees': ['monthly account', 'monthly fee', 'account fee', 'monthly acc fee'],
  'Cash Deposit Charges': ['cash deposit'],
  'Other System Charges': [
    'other system charge',
    'system charge',
    'system fee',
    'other bank charge',
    'bank charge',
    'bank fee',
    'transaction charge',
    'transaction charges',
    'admin charge',
    'administration charge',
    'proof of paymt',
    'proof of payment',
    'notific fee',
    'notifyme',
    'sms',
  ],
  'System Fees': [
    'system fee',
    'system fees',
    'bank fee',
    'bank fees',
    'service fee',
    'service charge',
    'transaction fee',
    'transaction charge',
    'admin charge',
  ],
  'General': [
    'interest',
    'interest paid',
    'interest received',
    'credit interest',
    'monthly interest',
  ],
};

export const normalizeKeyword = (keyword: string) => keyword.trim().toLowerCase();

const normalizeForMatching = (value: string) =>
  value
    .toLowerCase()
    .replace(/\bacc\b/g, 'account')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const keywordMatches = (description: string, keyword: string) => {
  const normalizedDescription = normalizeForMatching(description);
  const normalizedKeyword = normalizeForMatching(keyword);
  if (!normalizedDescription || !normalizedKeyword) return false;

  if (normalizedDescription.includes(normalizedKeyword)) {
    return true;
  }

  if (normalizedKeyword.length <= 3) {
    const descriptionTokens = new Set(normalizedDescription.split(' '));
    return descriptionTokens.has(normalizedKeyword);
  }

  return false;
};

const levenshteinDistance = (left: string, right: string): number => {
  if (!left.length) return right.length;
  if (!right.length) return left.length;

  const dp = Array.from({ length: left.length + 1 }, () => Array(right.length + 1).fill(0));
  for (let index = 0; index <= left.length; index += 1) dp[index][0] = index;
  for (let index = 0; index <= right.length; index += 1) dp[0][index] = index;

  for (let row = 1; row <= left.length; row += 1) {
    for (let column = 1; column <= right.length; column += 1) {
      const cost = left[row - 1] === right[column - 1] ? 0 : 1;
      dp[row][column] = Math.min(
        dp[row - 1][column] + 1,
        dp[row][column - 1] + 1,
        dp[row - 1][column - 1] + cost
      );
    }
  }

  return dp[left.length][right.length];
};

const tokenFuzzyMatches = (descriptionToken: string, keywordToken: string): boolean => {
  if (!descriptionToken || !keywordToken) return false;
  if (descriptionToken === keywordToken) return true;
  if (descriptionToken.length < 4 || keywordToken.length < 4) return false;
  if (descriptionToken.includes(keywordToken) || keywordToken.includes(descriptionToken)) return true;

  const distance = levenshteinDistance(descriptionToken, keywordToken);
  const threshold = Math.max(descriptionToken.length, keywordToken.length) >= 8 ? 2 : 1;
  if (distance > threshold) return false;
  return descriptionToken[0] === keywordToken[0];
};

const scoreCategoryMatch = (description: string, keywords: string[]): { score: number; matchedKeywords: number; bestKeywordLength: number } => {
  const normalizedDescription = normalizeForMatching(description);
  if (!normalizedDescription) return { score: 0, matchedKeywords: 0, bestKeywordLength: 0 };

  const descriptionTokens = normalizedDescription.split(' ').filter(Boolean);
  const keywordList = Array.from(new Set(keywords.map(normalizeForMatching).filter(Boolean)));

  let score = 0;
  let matchedKeywordCount = 0;
  let bestKeywordLength = 0;

  keywordList.forEach((keyword) => {
    if (keywordMatches(normalizedDescription, keyword)) {
      matchedKeywordCount += 1;
      score += keyword.length >= 6 ? 5 : 4;
      bestKeywordLength = Math.max(bestKeywordLength, keyword.length);
      return;
    }

    const keywordTokens = keyword.split(' ').filter(Boolean);
    if (keywordTokens.length === 0) return;

    const matchedTokens = keywordTokens.filter((keywordToken) =>
      descriptionTokens.some((descriptionToken) => tokenFuzzyMatches(descriptionToken, keywordToken))
    ).length;

    if (matchedTokens === 0) return;

    const coverage = matchedTokens / keywordTokens.length;
    if (coverage >= 1) {
      matchedKeywordCount += 1;
      score += 4;
      bestKeywordLength = Math.max(bestKeywordLength, keyword.length);
    } else if (coverage >= 0.5) {
      score += 2;
    } else {
      score += 1;
    }
  });

  if (matchedKeywordCount >= 2) score += 3;
  else if (matchedKeywordCount === 1) score += 1;

  return { score, matchedKeywords: matchedKeywordCount, bestKeywordLength };
};

export const isSystemFeeCategoryName = (name?: string): boolean => {
  return !!normalizeSystemFeeCategoryName(name);
};

export const buildSystemCategoryKeywordConfig = (holders: AccountHolder[]): Record<string, string[]> => {
  const config: Record<string, string[]> = {};

  CURRENT_SYSTEM_FEE_CATEGORIES.forEach((category) => {
    const holder = holders.find((item) => normalizeSystemFeeCategoryName(item.name) === category);
    const defaultKeywords = (DEFAULT_SYSTEM_CATEGORY_KEYWORDS[category] || []).map(normalizeKeyword).filter(Boolean);

    if (holder) {
      const configuredKeywords = (holder.keywords || []).map(normalizeKeyword).filter(Boolean);
      config[category] = Array.from(new Set(configuredKeywords));
      return;
    }

    config[category] = Array.from(new Set(defaultKeywords));
  });

  return config;
};

export const resolveSystemFeeCategory = (description?: string, keywordConfig?: Record<string, string[]>): string | null => {
  const rawDescription = description || '';
  if (!rawDescription.trim()) return null;

  const resolvedConfig = keywordConfig || DEFAULT_SYSTEM_CATEGORY_KEYWORDS;
  const categoryCandidates = Array.from(new Set([
    ...Object.keys(resolvedConfig || {}),
    ...ALL_SYSTEM_FEE_CATEGORIES,
  ]));
  const scoredMatches: Array<{ category: string; score: number; matchedKeywords: number; bestKeywordLength: number }> = [];

  for (const category of categoryCandidates) {
    const keywords = (resolvedConfig[category] || []).map(normalizeKeyword).filter(Boolean);
    if (keywords.length === 0) continue;
    const result = scoreCategoryMatch(rawDescription, keywords);
    if (result.score > 0) scoredMatches.push({ category, ...result });
  }

  if (scoredMatches.length === 0) return null;

  const ordered = [...scoredMatches].sort((first, second) => {
    if (second.score !== first.score) return second.score - first.score;
    if (second.matchedKeywords !== first.matchedKeywords) return second.matchedKeywords - first.matchedKeywords;
    return second.bestKeywordLength - first.bestKeywordLength;
  });
  if (ordered[0].matchedKeywords < 1 || ordered[0].score < 4) return null;
  return ordered[0].category;
};

export const resolveTransactionSystemCategory = (tx: Payment, keywordConfig?: Record<string, string[]>): string | null => {
  const mappedCategory = normalizeSystemFeeCategoryName(tx.accountHolderName);
  if (mappedCategory) return mappedCategory;
  return resolveSystemFeeCategory(tx.description, keywordConfig);
};
